import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertCircle, Euro, Search, Phone, CreditCard } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const Impayes = () => {
  const { token } = useAuth();
  const [data, setData] = useState({ impayes: [], stats: { nombre_impayes: 0, total_du: 0, total_encaisse_partiel: 0, total_prevu: 0 } });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentDialog, setPaymentDialog] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentNote, setPaymentNote] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchImpayes = async () => {
    try {
      const res = await axios.get(`${API}/impayes`, { headers });
      setData(res.data);
    } catch { toast.error("Erreur chargement impayés"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchImpayes(); }, []);

  const handlePaiement = async () => {
    if (!paymentDialog || !paymentAmount) return;
    try {
      await axios.put(`${API}/impayes/${paymentDialog.id}/paiement`, {
        montant: parseFloat(paymentAmount),
        note: paymentNote
      }, { headers });
      toast.success("Paiement enregistré !");
      setPaymentDialog(null);
      setPaymentAmount("");
      setPaymentNote("");
      fetchImpayes();
    } catch { toast.error("Erreur paiement"); }
  };

  const filtered = data.impayes.filter(i =>
    i.client_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.client_telephone?.includes(searchTerm)
  );

  const { stats } = data;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="impayes-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-800">Impayés</h1>
        <p className="text-slate-500 mt-1">Suivi des paiements partiels et montants restants</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-sm text-red-600">Total impayé</p>
            <p className="text-2xl font-bold text-red-700" data-testid="total-impaye">{stats.total_du.toFixed(2)} €</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Nombre d'impayés</p>
            <p className="text-2xl font-bold text-slate-800" data-testid="count-impayes">{stats.nombre_impayes}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Encaissé partiel</p>
            <p className="text-2xl font-bold text-amber-600">{stats.total_encaisse_partiel.toFixed(2)} €</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total prévu</p>
            <p className="text-2xl font-bold text-indigo-600">{stats.total_prevu.toFixed(2)} €</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input placeholder="Rechercher par nom ou téléphone..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-impayes" />
          </div>
        </CardContent>
      </Card>

      {/* Payment dialog */}
      <Dialog open={!!paymentDialog} onOpenChange={o => { if (!o) setPaymentDialog(null); }}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle>Enregistrer un paiement</DialogTitle></DialogHeader>
          {paymentDialog && (
            <div className="space-y-4 mt-2">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{paymentDialog.client_nom}</p>
                <p className="text-sm text-slate-500">Restant dû: <strong className="text-red-600">{paymentDialog.montant_du?.toFixed(2)} €</strong></p>
              </div>
              <div className="space-y-1">
                <Label>Montant du paiement (€)</Label>
                <Input type="number" step="0.01" min="0.01" max={paymentDialog.montant_du} value={paymentAmount}
                  onChange={e => setPaymentAmount(e.target.value)} placeholder="0.00" data-testid="payment-amount-input" />
              </div>
              <div className="space-y-1">
                <Label>Note (optionnel)</Label>
                <Input value={paymentNote} onChange={e => setPaymentNote(e.target.value)} placeholder="Ex: Virement reçu" data-testid="payment-note-input" />
              </div>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handlePaiement} data-testid="submit-payment-btn">
                <CreditCard className="w-4 h-4 mr-2" /> Enregistrer le paiement
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Card data-testid="impayes-table-card">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><AlertCircle className="w-5 h-5 text-red-600" />Livraisons impayées ({filtered.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Euro className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">Aucun impayé !</p>
              <p className="text-sm mt-1">Tous les paiements sont à jour</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead>Commande</TableHead>
                  <TableHead className="text-right">Prix</TableHead>
                  <TableHead className="text-right">Encaissé</TableHead>
                  <TableHead className="text-right">Restant dû</TableHead>
                  <TableHead>% Payé</TableHead>
                  <TableHead className="w-28"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(i => (
                  <TableRow key={i.id} data-testid={`impaye-row-${i.id}`}>
                    <TableCell className="font-medium">{i.client_nom}</TableCell>
                    <TableCell>
                      {i.client_telephone ? (
                        <a href={`tel:${i.client_telephone}`} className="flex items-center gap-1 text-indigo-600 hover:underline">
                          <Phone className="w-3 h-3" />{i.client_telephone}
                        </a>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-sm max-w-48 truncate">{i.details_commande}</TableCell>
                    <TableCell className="text-right">{(i.prix || 0).toFixed(2)} €</TableCell>
                    <TableCell className="text-right text-amber-600">{(i.montant_encaisse || 0).toFixed(2)} €</TableCell>
                    <TableCell className="text-right font-bold text-red-600">{(i.montant_du || 0).toFixed(2)} €</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-amber-500" style={{ width: `${i.pourcentage_paye || 0}%` }}></div>
                        </div>
                        <span className="text-xs text-slate-500">{(i.pourcentage_paye || 0).toFixed(0)}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button size="sm" onClick={() => { setPaymentDialog(i); setPaymentAmount(""); setPaymentNote(""); }}
                        className="bg-emerald-600 hover:bg-emerald-700" data-testid={`pay-btn-${i.id}`}>
                        <CreditCard className="w-3 h-3 mr-1" /> Payer
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Impayes;
